/**
 * File uploads that belong to a template editor, not to the file popup.
 *
 * Closing a File cell's popup used to orphan its uploads: the browser kept
 * sending, but the popup that would have put the finished file in the cell was
 * gone. Here the editor owns them, keyed by the cell they belong to, so they
 * run on with the popup closed, the table cell shows their progress, and a
 * finished one lands in its cell through `onDone`.
 *
 * Every upload carries an AbortController (lib/files): cancelling one releases
 * what the server holds for it, so the allowance drops straight back. The
 * editor cancels a cell's uploads when its row or column goes, and all of
 * them when it closes (on unmount, whatever the way out).
 *
 * `cell` is the editor's own description of the cell ({ rowId, columnId } or
 * { level, itemKey, subKey, columnId }); `keyOf(cell)` turns it into the key
 * uploads are grouped by.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { quotaProblem } from './fileLimits';
import { isCancelled } from './files';

let nextKey = 1;

/**
 * @param service  the lib/files service (upload, usage)
 * @param opts.keyOf(cell) → string
 * @param opts.onDone(cell, { fileId, name, size })  a finished upload, for its cell
 */
export function useCellUploads(service, { keyOf, onDone }) {
  const [uploads, setUploads] = useState([]); // [{ key, cellKey, cell, name, size, pct, error }]
  const [usage, setUsage] = useState(null); // { files, bytes, limits, plan } once read
  const [usageError, setUsageError] = useState(false);
  const controllers = useRef(new Map()); // upload key → AbortController
  const alive = useRef(true);
  const latest = useRef({ keyOf, onDone });
  latest.current = { keyOf, onDone };

  useEffect(() => {
    alive.current = true;
    const running = controllers.current;
    return () => {
      // the editor is closing: nothing will hold these files
      alive.current = false;
      for (const c of running.values()) c.abort();
      running.clear();
    };
  }, []);

  const patch = (key, part) => setUploads((prev) => prev.map((u) => (u.key === key ? { ...u, ...part } : u)));
  const drop = (key) => setUploads((prev) => prev.filter((u) => u.key !== key));

  /** Reads the allowance again (each time a popup opens). */
  const loadUsage = useCallback(() => {
    if (!service) return;
    setUsageError(false);
    Promise.resolve()
      .then(() => service.usage())
      .then((u) => {
        if (!alive.current) return;
        if (!u || u.error) setUsageError(true);
        else setUsage(u);
      })
      .catch(() => alive.current && setUsageError(true));
  }, [service]);

  /** Uploads `picked` into `cell`; a file over the allowance shows its reason without asking the server. */
  const start = (cell, picked) => {
    const cellKey = latest.current.keyOf(cell);
    const limits = usage && usage.limits;
    for (const file of picked) {
      const key = nextKey++;
      const problem = limits ? quotaProblem(usage, limits, { name: file.name, size: file.size }) : null;
      setUploads((prev) => [...prev, { key, cellKey, cell, name: file.name, size: file.size, pct: 0, error: problem }]);
      if (problem) continue;
      const ctrl = new AbortController();
      controllers.current.set(key, ctrl);
      service
        .upload(file, { usage, limits, signal: ctrl.signal, onProgress: (pct) => alive.current && patch(key, { pct }) })
        .then((done) => {
          controllers.current.delete(key);
          if (!alive.current) return;
          drop(key);
          setUsage((u) => (u ? { ...u, files: u.files + 1, bytes: u.bytes + (done.size || 0) } : u));
          latest.current.onDone(cell, { fileId: done.fileId, name: done.name, size: done.size });
        })
        .catch((e) => {
          controllers.current.delete(key);
          if (!alive.current) return;
          if (isCancelled(e)) drop(key);
          else patch(key, { error: (e && e.message) || 'Upload failed' });
        });
    }
  };

  /** Stops a running upload (its server side is released), or clears a failed one. */
  const cancel = (key) => {
    const ctrl = controllers.current.get(key);
    if (ctrl) ctrl.abort();
    drop(key);
  };

  /** Cancels every upload whose cell matches, e.g. a deleted row's or a removed column's. */
  const cancelWhere = (match) => {
    for (const u of uploads) if (match(u.cell)) cancel(u.key);
  };

  const cancelAll = () => cancelWhere(() => true);

  const byCell = useMemo(() => {
    const m = new Map();
    for (const u of uploads) m.set(u.cellKey, [...(m.get(u.cellKey) || []), u]);
    return m;
  }, [uploads]);

  const running = uploads.filter((u) => !u.error).length;

  return {
    forCell: (cell) => byCell.get(latest.current.keyOf(cell)) || [],
    start,
    cancel,
    cancelWhere,
    cancelAll,
    running,
    usage,
    usageError,
    loadUsage,
  };
}

/** "Wait for 2 uploads to finish", for the Save button's title. */
export function waitForUploadsMessage(running) {
  return `Wait for ${running} upload${running === 1 ? '' : 's'} to finish`;
}

/** "3 files are still uploading", for the leave confirm. */
export function stillUploadingTitle(running) {
  return running === 1 ? '1 file is still uploading' : `${running} files are still uploading`;
}
